/**
 * Filtros para el listado de cuentas del BO.
 *
 * @taskId T473
 * @see functional/Specs/specs_cycle_4.md HU-27-FE
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Search, X } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@flexibilitypay/ui';

import type { AccountStatus } from '../types/account.types';

export interface AccountFiltersProps {
  onSearch: (search: string) => void;
  onStatusFilter: (status: AccountStatus | undefined) => void;
  onExport: () => void;
  isExporting: boolean;
  currentSearch: string;
  currentStatus?: AccountStatus;
}

const STATUS_OPTIONS: { value: AccountStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Activa' },
  { value: 'SUSPENDED', label: 'Suspendida' },
  { value: 'BLOCKED', label: 'Bloqueada' },
  { value: 'DEACTIVATED', label: 'Desactivada' },
  { value: 'CLOSED', label: 'Cerrada' },
];

export function AccountFilters({
  onSearch,
  onStatusFilter,
  onExport,
  isExporting,
  currentSearch,
  currentStatus,
}: AccountFiltersProps) {
  const { t } = useTranslation(['accounts', 'common']);
  const [searchValue, setSearchValue] = useState(currentSearch);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSearch(searchValue);
    },
    [onSearch, searchValue],
  );

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    onSearch('');
  }, [onSearch]);

  const handleStatusChange = useCallback(
    (value: string) => {
      onStatusFilter(value === 'ALL' ? undefined : (value as AccountStatus));
    },
    [onStatusFilter],
  );

  return (
    <div className="mb-6 flex flex-wrap items-center gap-4">
      {/* Búsqueda */}
      <form onSubmit={handleSearchSubmit} className="flex flex-1 items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t('accounts:filters.searchPlaceholder', 'Buscar por CVU, alias o merchant...')}
            className="pl-10 pr-10"
          />
          {searchValue && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button type="submit" variant="secondary">
          {t('common:search', 'Buscar')}
        </Button>
      </form>

      {/* Filtro por estado */}
      <Select value={currentStatus || 'ALL'} onValueChange={handleStatusChange}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder={t('accounts:filters.statusPlaceholder', 'Todos los estados')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">{t('accounts:filters.allStatuses', 'Todos los estados')}</SelectItem>
          {STATUS_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Exportar CSV */}
      <Button variant="outline" onClick={onExport} disabled={isExporting}>
        <Download className="mr-2 h-4 w-4" />
        {isExporting ? t('common:exporting', 'Exportando...') : t('common:exportCsv', 'Exportar CSV')}
      </Button>
    </div>
  );
}
